import { Helmet } from 'react-helmet-async';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Popover,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Icon } from '@iconify/react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import Services from 'src/services/Services';
import { tokenService } from 'src/services';
import CountryList from 'src/data/CountryList';
import { formatPhoneDisplay } from 'src/utils/phoneFormat';
import { StatusList } from 'src/types/statusTypes';
import { getPreferredPostFromResults } from 'src/utils/postFlowRules';

// Servis tek istekte en fazla bu kadar kayıt döndürüyor.
const FETCH_LIMIT = 200;

const CURRENCY_SYMBOLS = { EUR: '€', TRY: '₺', USD: '$', GBP: '£' };

const PAYMENT_FILTERS = [
  { value: '', label: 'Ödeme Durumu' },
  { value: '1', label: 'Ödendi' },
  { value: '0', label: 'Ödenmedi' },
];

const TABLE_HEAD = [
  'Oluşturma Tarihi',
  'Waybill',
  'Sipariş No',
  'Nokta',
  'Gönderici',
  'Alıcı',
  'Tutar',
  'Ödeme',
  'Durum',
  'Detay',
  '',
];

// get-shipments tarihi "2026-09-11 14:42:18" biçiminde, set-shipment-paid ise Unix
// saniye olarak dönüyor; ikisi de karşılanıyor.
function parseDate(value) {
  if (value === null || value === undefined || value === '') return null;
  const date = /^\d+$/.test(String(value))
    ? moment.unix(Number(value))
    : moment(value, ['YYYY-MM-DD HH:mm:ss', moment.ISO_8601], true);
  return date.isValid() ? date : null;
}

function formatDate(value) {
  const date = parseDate(value);
  return date ? date.format('DD.MM.YYYY HH:mm') : '-';
}

function formatMoney(amount, currency) {
  if (amount === null || amount === undefined || amount === '') return '-';
  const number = Number(amount);
  if (!Number.isFinite(number)) return '-';
  return `${number.toFixed(2)} ${CURRENCY_SYMBOLS[currency] || currency || ''}`.trim();
}

function getCountryName(code) {
  if (!code) return '-';
  const country = CountryList.find((item) => item.code === code);
  return country ? `${country.name} (${code})` : code;
}

const valueOrDash = (value) => (value === null || value === undefined || value === '' ? '-' : value);

function getErrorMessage(error, fallback) {
  return error?.response?.data?.message || error?.message || fallback;
}

const CHIP_TONES = {
  success: { color: '#1E7A45', background: '#E8F6EE', dot: '#22A45D' },
  warning: { color: '#B25E09', background: '#FDF3E6', dot: '#F0A02A' },
  error: { color: '#B42318', background: '#FEF3F2', dot: '#F04438' },
  info: { color: '#175CD3', background: '#EFF8FF', dot: '#2E90FA' },
};

function PaymentChip({ isPaid }) {
  return <StatusChip tone={isPaid ? 'success' : 'warning'} label={isPaid ? 'Ödendi' : 'Ödenmedi'} />;
}

function StatusChip({ tone, label }) {
  const style = CHIP_TONES[tone] || CHIP_TONES.info;
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.75}
      sx={{
        display: 'inline-flex',
        backgroundColor: style.background,
        color: style.color,
        borderRadius: 1,
        px: 1.25,
        py: 0.5,
        fontSize: 13,
        fontWeight: 500,
        whiteSpace: 'nowrap',
      }}
    >
      <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: style.dot }} />
      <span>{label}</span>
    </Stack>
  );
}

function DetailSection({ title, rows }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography sx={{ fontWeight: 700, color: '#101828', mb: 1 }}>{title}</Typography>
      <Box sx={{ border: '1px solid #EAECF0', borderRadius: 1 }}>
        {rows.map(([label, value], index) => (
          <Stack
            key={label}
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 0.25, sm: 2 }}
            sx={{ px: 2, py: 1.25, borderTop: index === 0 ? 'none' : '1px solid #EAECF0' }}
          >
            <Typography sx={{ color: '#667085', fontSize: 14, minWidth: 180 }}>{label}</Typography>
            <Typography component="div" sx={{ color: '#101828', fontSize: 14, wordBreak: 'break-word' }}>{value}</Typography>
          </Stack>
        ))}
      </Box>
    </Box>
  );
}

function getPartyRows(party) {
  return [
    ['Ad Soyad', valueOrDash(party?.name)],
    ['Firma', valueOrDash(party?.company)],
    ['Telefon', valueOrDash(formatPhoneDisplay(party?.phone, party?.phoneCountryCode || party?.phoneCode))],
    ...(party?.email !== undefined ? [['E-posta', valueOrDash(party.email)]] : []),
    ['Adres', valueOrDash(party?.address)],
    ['Şehir / Posta Kodu', `${valueOrDash(party?.city)} / ${valueOrDash(party?.postalCode)}`],
    ['Ülke', getCountryName(party?.countryCode)],
  ];
}

// DHL makbuz istendiğinde faturayı ve makbuzu tek PDF'te "invoice+receipt" olarak döndürüyor.
const DOCUMENT_LABELS = {
  label: 'Gönderi Etiketi',
  invoice: 'Fatura',
  receipt: 'Gönderi Makbuzu',
  'invoice+receipt': 'Fatura ve Makbuz',
};

// Gönderinin sistemdeki durumu: paket takip numarası (barkod) post-search-barcode ile
// sorgulanır, durum kodu diğer sayfalardaki ortak listeyle ada çevrilir.
const getPostStatusLabel = (status) =>
  StatusList.find((item) => String(item.id) === String(status))?.label || (status ? `Durum ${status}` : '-');

// Tabloda gönderinin ilk paketinin durumu gösterilir.
const getPrimaryBarcode = (item) => (item.packageTrackingNumbers || [])[0] || '';

// Durumun rengi adından çıkarılır; liste uzun olduğu için kod kod eşleme yapılmıyor.
const getPostStatusTone = (label) => {
  const text = label.toLocaleLowerCase('tr');
  if (['iptal', 'iade', 'kayıp', 'zarar', 'parçalandı', 'dolandır'].some((word) => text.includes(word))) return 'error';
  if (text.includes('tamamlandı') || text.includes('teslim edildi')) return 'success';
  if (text.includes('bekle')) return 'warning';
  return 'info';
};

// chip: tabloda renkli etiket; değilse (detay) düz metin.
function PostStatusCell({ entry, chip = false }) {
  if (!entry || entry.loading) return <CircularProgress size={16} />;
  if (entry.error) return <Typography sx={{ fontSize: 13, color: '#B42318' }}>Alınamadı</Typography>;
  if (!entry.post) return '-';
  const label = getPostStatusLabel(entry.post.status);
  if (chip) return <StatusChip tone={getPostStatusTone(label)} label={label} />;
  return <Typography sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>{label}</Typography>;
}

export default function DHLShipments() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  // Sunucu tarafı filtreler: değişince yeniden istek atılır.
  const [paymentFilter, setPaymentFilter] = useState('');
  const [range, setRange] = useState({ start: moment().subtract(30, 'days'), end: moment() });
  const [dateAnchor, setDateAnchor] = useState(null);
  const [draftStart, setDraftStart] = useState(null);
  const [draftEnd, setDraftEnd] = useState(null);

  // Arama gelen liste içinde yapılır: waybill ve sipariş numarası ikisi de 10 haneli
  // olduğu için tekil sorguda hangisinin yazıldığı ayırt edilemiyor.
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [detail, setDetail] = useState(null);

  // Satırdaki üç nokta menüsü ve işlem onayı.
  const [actionMenu, setActionMenu] = useState(null); // { anchor, item }
  const [confirmAction, setConfirmAction] = useState(null); // { type: 'cancel' | 'dropoff', item }
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState(null); // { severity, message }

  // barkod -> { loading, error, post }. Bir kez sorgulanan barkod tekrar sorgulanmaz;
  // liste yenilenince önbellek temizlenir.
  const [postStatuses, setPostStatuses] = useState({});
  const requestedBarcodesRef = useRef(new Set());

  const loadPostStatuses = useCallback(async (barcodes) => {
    const missing = [...new Set(barcodes.filter(Boolean))].filter((code) => !requestedBarcodesRef.current.has(code));
    if (missing.length === 0) return;
    missing.forEach((code) => requestedBarcodesRef.current.add(code));
    setPostStatuses((prev) => ({ ...prev, ...Object.fromEntries(missing.map((code) => [code, { loading: true }])) }));
    try {
      const response = await Services.SearchPostsByBarcodes(tokenService.getUser()?.token, missing);
      const results = response?.data?.status && Array.isArray(response.data.result) ? response.data.result : [];
      // Aynı barkoda birden fazla kayıt dönebiliyor; en güncel olan seçilir.
      const next = Object.fromEntries(
        missing.map((code) => [
          code,
          { post: getPreferredPostFromResults(results.filter((post) => post.barcode === code)) },
        ])
      );
      setPostStatuses((prev) => ({ ...prev, ...next }));
    } catch (error) {
      console.error('Gönderi durumları alınamadı:', error);
      missing.forEach((code) => requestedBarcodesRef.current.delete(code));
      setPostStatuses((prev) => ({ ...prev, ...Object.fromEntries(missing.map((code) => [code, { error: true }])) }));
    }
  }, []);

  // İşlemden sonra paketin durumu yeniden sorgulanır.
  const refreshPostStatus = useCallback(
    (code) => {
      requestedBarcodesRef.current.delete(code);
      loadPostStatuses([code]);
    },
    [loadPostStatuses]
  );

  // İptal: paketin sistemdeki kaydı posts/cancel ile İptal (2) durumuna çekilir.
  // Zimmete al: kaydı olmayan paket dhl-drop-off-accept ile paket numarasıyla zimmete alınır.
  const runConfirmedAction = async () => {
    if (!confirmAction) return;
    const { type, item } = confirmAction;
    const code = getPrimaryBarcode(item);
    const token = tokenService.getUser()?.token;
    setActionLoading(true);
    try {
      const response =
        type === 'cancel'
          ? await Services.PostCancel(token, postStatuses[code]?.post?.id)
          : await Services.DHLDropOffAccept(token, code);
      if (!response?.data?.status) throw new Error(response?.data?.message || 'İşlem başarısız oldu.');
      setToast({
        severity: 'success',
        message: type === 'cancel' ? 'Gönderi iptal edildi.' : 'Gönderi zimmete alındı.',
      });
      setConfirmAction(null);
      refreshPostStatus(code);
    } catch (error) {
      setToast({
        severity: 'error',
        message: `${type === 'cancel' ? 'Gönderi iptal edilemedi' : 'Gönderi zimmete alınamadı'}: ${getErrorMessage(error, 'bilinmeyen hata')}`,
      });
    } finally {
      setActionLoading(false);
    }
  };

  const fetchShipments = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const filters = { limit: FETCH_LIMIT };
      if (paymentFilter !== '') filters.isPaid = Number(paymentFilter);
      if (range.start) filters.startDate = moment(range.start).format('YYYY-MM-DD');
      if (range.end) filters.endDate = moment(range.end).format('YYYY-MM-DD');

      const response = await Services.GetDHLShipments(tokenService.getUser()?.token, filters);
      if (response?.data?.status === false) {
        throw new Error(response.data.message || 'Gönderiler alınamadı.');
      }
      const list = response?.data?.result?.items;
      requestedBarcodesRef.current = new Set();
      setPostStatuses({});
      setItems(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error('DHL gönderileri alınamadı:', error);
      setItems([]);
      setLoadError(getErrorMessage(error, 'Gönderiler alınamadı.'));
    } finally {
      setLoading(false);
    }
  }, [paymentFilter, range]);

  useEffect(() => {
    fetchShipments();
  }, [fetchShipments]);

  const filteredItems = useMemo(() => {
    const query = search.trim().toLocaleLowerCase('tr');
    const sorted = [...items].sort(
      (a, b) => (parseDate(b.createdDateTime)?.valueOf() || 0) - (parseDate(a.createdDateTime)?.valueOf() || 0)
    );
    if (!query) return sorted;
    return sorted.filter((item) =>
      [
        item.shipmentTrackingNumber,
        item.orderNumber,
        ...(item.packageTrackingNumbers || []),
        item.poiTitle,
        item.sender?.name,
        item.sender?.company,
        item.receiver?.name,
        item.receiver?.company,
      ].some((field) => String(field || '').toLocaleLowerCase('tr').includes(query))
    );
  }, [items, search]);

  // Liste yenilenip kısalırsa sayfa numarası aralık dışında kalmasın.
  const lastPage = Math.max(0, Math.ceil(filteredItems.length / rowsPerPage) - 1);
  const currentPage = Math.min(page, lastPage);
  const pagedItems = useMemo(
    () => filteredItems.slice(currentPage * rowsPerPage, currentPage * rowsPerPage + rowsPerPage),
    [filteredItems, currentPage, rowsPerPage]
  );

  // Yalnızca ekrandaki satırların durumu, tek istekte sorgulanır.
  useEffect(() => {
    loadPostStatuses(pagedItems.map(getPrimaryBarcode));
  }, [pagedItems, loadPostStatuses]);

  // Detayda gönderinin bütün paketlerinin durumu gösterilir.
  useEffect(() => {
    if (detail) loadPostStatuses(detail.packageTrackingNumbers || []);
  }, [detail, loadPostStatuses]);

  const rangeLabel = range.start || range.end
    ? `${range.start ? moment(range.start).format('DD/MM/YYYY') : '…'} - ${range.end ? moment(range.end).format('DD/MM/YYYY') : '…'}`
    : 'Tüm Tarihler';

  const openDatePopover = (event) => {
    setDraftStart(range.start);
    setDraftEnd(range.end);
    setDateAnchor(event.currentTarget);
  };

  const applyRange = () => {
    setRange({ start: draftStart, end: draftEnd });
    setDateAnchor(null);
    setPage(0);
  };

  const clearRange = () => {
    setRange({ start: null, end: null });
    setDateAnchor(null);
    setPage(0);
  };

  return (
    <>
      <Helmet>
        <title>DHL Gönderileri</title>
      </Helmet>

      <Stack sx={{ px: 3 }}>
        <Typography variant="h4" sx={{ mb: 4 }}>
          DHL Gönderileri
        </Typography>

        <Card sx={{ p: 3 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', md: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Box>
              <Typography variant="h6">Gönderi Kayıtları</Typography>
              {!loading && !loadError && (
                <Typography sx={{ color: '#667085', fontSize: 13 }}>
                  {filteredItems.length} kayıt
                  {items.length >= FETCH_LIMIT && ` · ilk ${FETCH_LIMIT} kayıt gösteriliyor, tarih aralığını daraltın`}
                </Typography>
              )}
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                variant="outlined"
                color="inherit"
                onClick={openDatePopover}
                startIcon={<Icon icon="mdi:calendar-blank-outline" width={20} height={20} />}
                endIcon={<Icon icon="mdi:chevron-down" width={20} height={20} />}
                sx={{ height: 44, minWidth: 200, justifyContent: 'space-between', color: '#344054', whiteSpace: 'nowrap' }}
              >
                {rangeLabel}
              </Button>

              <Popover
                open={Boolean(dateAnchor)}
                anchorEl={dateAnchor}
                onClose={() => setDateAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{ sx: { p: 2.5, mt: 1 } }}
              >
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <DatePicker
                      label="Başlangıç"
                      format="DD/MM/YYYY"
                      value={draftStart}
                      onChange={(value) => {
                        setDraftStart(value);
                        if (draftEnd && value && moment(value).isAfter(draftEnd)) setDraftEnd(null);
                      }}
                    />
                    <DatePicker
                      label="Bitiş"
                      format="DD/MM/YYYY"
                      value={draftEnd}
                      minDate={draftStart || undefined}
                      onChange={(value) => setDraftEnd(value)}
                    />
                  </Stack>
                </LocalizationProvider>
                <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ mt: 2.5 }}>
                  <Button variant="outlined" color="inherit" onClick={clearRange} sx={{ color: '#344054' }}>
                    Tüm Tarihler
                  </Button>
                  <Button variant="contained" disableElevation onClick={applyRange} sx={{ boxShadow: 'none' }}>
                    Uygula
                  </Button>
                </Stack>
              </Popover>

              <TextField
                select
                size="small"
                value={paymentFilter}
                onChange={(event) => {
                  setPaymentFilter(event.target.value);
                  setPage(0);
                }}
                SelectProps={{ displayEmpty: true }}
                sx={{ minWidth: 170, '& .MuiInputBase-root': { height: 44 } }}
              >
                {PAYMENT_FILTERS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                size="small"
                placeholder="Waybill, sipariş no, isim..."
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(0);
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Icon icon="eva:search-fill" width={20} height={20} color="#98A2B3" />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 240, '& .MuiInputBase-root': { height: 44 } }}
              />

              <IconButton
                onClick={fetchShipments}
                disabled={loading}
                title="Yenile"
                sx={{ border: '1px solid #D0D5DD', borderRadius: 1, width: 44, height: 44 }}
              >
                <Icon icon="mdi:refresh" width={20} height={20} />
              </IconButton>
            </Stack>
          </Stack>

          <Box sx={{ overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow>
                  {TABLE_HEAD.map((label) => (
                    <TableCell key={label} sx={{ whiteSpace: 'nowrap' }}>
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={TABLE_HEAD.length} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                )}

                {!loading && loadError && (
                  <TableRow>
                    <TableCell colSpan={TABLE_HEAD.length} align="center" sx={{ py: 6 }}>
                      <Typography sx={{ color: '#B42318', mb: 2 }}>{loadError}</Typography>
                      <Button variant="outlined" color="inherit" onClick={fetchShipments} sx={{ color: '#344054' }}>
                        Tekrar Dene
                      </Button>
                    </TableCell>
                  </TableRow>
                )}

                {!loading && !loadError && pagedItems.map((item) => (
                  <TableRow key={item.id ?? item.shipmentTrackingNumber} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(item.createdDateTime)}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{valueOrDash(item.shipmentTrackingNumber)}</TableCell>
                    <TableCell>{valueOrDash(item.orderNumber)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{valueOrDash(item.poiTitle)}</TableCell>
                    <TableCell>{valueOrDash(item.sender?.name)}</TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: 14 }}>{valueOrDash(item.receiver?.name)}</Typography>
                      <Typography sx={{ fontSize: 12, color: '#667085' }}>
                        {[item.receiver?.city, item.receiver?.countryCode].filter(Boolean).join(', ') || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatMoney(item.paidPrice, item.paidPriceCurrency)}</TableCell>
                    <TableCell>
                      <PaymentChip isPaid={Boolean(item.isPaid)} />
                    </TableCell>
                    <TableCell>
                      {getPrimaryBarcode(item) ? <PostStatusCell chip entry={postStatuses[getPrimaryBarcode(item)]} /> : '-'}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        color="inherit"
                        size="small"
                        onClick={() => setDetail(item)}
                        sx={{ color: '#344054', minWidth: 90 }}
                      >
                        Detay
                      </Button>
                    </TableCell>
                    <TableCell align="right" sx={{ width: 48 }}>
                      <IconButton
                        size="small"
                        aria-label="İşlemler"
                        onClick={(e) => setActionMenu({ anchor: e.currentTarget, item })}
                      >
                        <Icon icon="mdi:dots-vertical" width={20} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}

                {!loading && !loadError && filteredItems.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={TABLE_HEAD.length} align="center" sx={{ py: 6, color: '#667085' }}>
                      Kayıt bulunamadı
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>

          <TablePagination
            component="div"
            count={filteredItems.length}
            page={currentPage}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
            labelRowsPerPage="Sayfa başına"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
          />
        </Card>
      </Stack>

      <Dialog open={Boolean(detail)} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        <DialogContent sx={{ p: { xs: 2.5, sm: 4 } }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 2 }}>
            <Box>
              <Typography variant="h6">Gönderi Detayı</Typography>
              {detail && (
                <Typography sx={{ color: '#667085', fontSize: 14 }}>
                  Waybill {valueOrDash(detail.shipmentTrackingNumber)}
                </Typography>
              )}
            </Box>
            <IconButton onClick={() => setDetail(null)} sx={{ mt: -1, mr: -1 }}>
              <CloseIcon />
            </IconButton>
          </Stack>

          {detail && (
            <>
              <DetailSection
                title="Gönderi"
                rows={[
                  ['Waybill', valueOrDash(detail.shipmentTrackingNumber)],
                  ['Sipariş No', valueOrDash(detail.orderNumber)],
                  ...((detail.packageTrackingNumbers || []).length
                    ? detail.packageTrackingNumbers.map((code, index, all) => [
                        all.length > 1 ? `Paket ${index + 1}` : 'Paket Takip No',
                        <Stack key={code} direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                          <span>{code}</span>
                          {/* Kaydı bulunmayan pakette yalnızca numara görünür. */}
                          {(!postStatuses[code] || postStatuses[code].loading || postStatuses[code].error || postStatuses[code].post) && (
                            <>
                              <span style={{ color: '#98A2B3' }}>·</span>
                              <PostStatusCell entry={postStatuses[code]} />
                            </>
                          )}
                        </Stack>,
                      ])
                    : [['Paket Takip No', '-']]),
                  ['Gönderi Türü', detail.isDocument ? 'Döküman' : 'Paket'],
                  ['Ürün Kodu', valueOrDash(detail.productCode)],
                  ['Açıklama', valueOrDash(detail.labelDescription)],
                  ['İhracat Tipi', valueOrDash(detail.exportReasonType)],
                  ['PLT', detail.pltAvailable ? 'Evet' : 'Hayır'],
                ]}
              />

              <DetailSection
                title="Nokta"
                rows={[
                  ['Nokta', valueOrDash(detail.poiTitle)],
                  ['Nokta ID', valueOrDash(detail.poiID)],
                  ['DHL Hesap No', valueOrDash(detail.dhlAccountNumber)],
                ]}
              />

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <DetailSection title="Gönderici" rows={getPartyRows(detail.sender)} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DetailSection title="Alıcı" rows={getPartyRows(detail.receiver)} />
                </Grid>
              </Grid>

              <DetailSection
                title="Tutarlar"
                rows={[
                  ['Beyan Edilen Değer', formatMoney(detail.declaredValue, detail.declaredValueCurrency)],
                  ['Ödenen Tutar', formatMoney(detail.paidPrice, detail.paidPriceCurrency)],
                  ['Sigorta', detail.withInsurance ? 'Var' : 'Yok'],
                  ...(detail.withInsurance
                    ? [['Sigortalanan Değer', formatMoney(detail.insuranceValue, detail.declaredValueCurrency)]]
                    : []),
                ]}
              />

              <DetailSection
                title="İşlem Geçmişi"
                rows={[
                  ['Oluşturan', valueOrDash(detail.createdByName)],
                  ['Oluşturma Tarihi', formatDate(detail.createdDateTime)],
                  ['Ödeme Durumu', detail.isPaid ? 'Ödendi' : 'Ödenmedi'],
                  ['Ödendi Olarak İşaretleyen', valueOrDash(detail.paidByName)],
                  ['Ödeme Tarihi', formatDate(detail.paidDateTime)],
                ]}
              />

              <Typography sx={{ fontWeight: 700, color: '#101828', mb: 1 }}>Belgeler</Typography>
              {(detail.documents || []).length === 0 ? (
                <Typography sx={{ color: '#667085', fontSize: 14 }}>Belge bulunamadı.</Typography>
              ) : (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  {detail.documents.map((document) => (
                    <Button
                      key={`${document.typeCode}-${document.url}`}
                      variant="outlined"
                      color="inherit"
                      href={document.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      disabled={!document.url}
                      startIcon={<Icon icon="mdi:file-pdf-box" width={20} height={20} />}
                      sx={{ color: '#344054', justifyContent: 'flex-start' }}
                    >
                      {DOCUMENT_LABELS[document.typeCode] || document.typeCode}
                    </Button>
                  ))}
                </Stack>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {actionMenu && (() => {
        const { item } = actionMenu;
        const entry = postStatuses[getPrimaryBarcode(item)];
        const post = entry?.post;
        const statusKnown = Boolean(entry) && !entry.loading && !entry.error;
        const alreadyCancelled = String(post?.status) === '2';
        // Zimmete al yalnızca sistemde kaydı (durumu) olmayan pakette gösterilir.
        const canDropOff = statusKnown && !post && Boolean(getPrimaryBarcode(item));
        const cancelHint = !post
          ? (statusKnown ? 'Sistemde kaydı yok' : 'Durum yükleniyor')
          : alreadyCancelled
            ? 'Zaten iptal edilmiş'
            : null;
        const openConfirm = (type) => {
          setConfirmAction({ type, item });
          setActionMenu(null);
        };
        return (
          <Menu
            open
            anchorEl={actionMenu.anchor}
            onClose={() => setActionMenu(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ sx: { minWidth: 200 } }}
          >
            {canDropOff && (
              <MenuItem onClick={() => openConfirm('dropoff')}>
                <ListItemIcon>
                  <Icon icon="mdi:package-variant-closed-check" width={20} />
                </ListItemIcon>
                <ListItemText primary="Zimmete Al" />
              </MenuItem>
            )}
            <MenuItem disabled={Boolean(cancelHint)} onClick={() => openConfirm('cancel')} sx={{ color: '#D92D20' }}>
              <ListItemIcon sx={{ color: '#D92D20' }}>
                <Icon icon="mdi:close-circle-outline" width={20} />
              </ListItemIcon>
              <ListItemText primary="İptal Et" secondary={cancelHint} />
            </MenuItem>
          </Menu>
        );
      })()}

      <Dialog open={Boolean(confirmAction)} onClose={actionLoading ? undefined : () => setConfirmAction(null)} maxWidth="xs" fullWidth>
        <DialogContent sx={{ p: 3, textAlign: 'center' }}>
          <Box sx={{ color: confirmAction?.type === 'cancel' ? '#D92D20' : '#175CD3', mb: 1 }}>
            <Icon
              icon={confirmAction?.type === 'cancel' ? 'mdi:close-circle-outline' : 'mdi:package-variant-closed-check'}
              width={44}
            />
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: 18, color: '#101828', mb: 1 }}>
            {confirmAction?.type === 'cancel' ? 'Gönderiyi iptal et' : 'Gönderiyi zimmete al'}
          </Typography>
          <Typography sx={{ color: '#475467', fontSize: 15 }}>
            <b>{valueOrDash(confirmAction?.item?.shipmentTrackingNumber)}</b> waybill numaralı gönderiyi{' '}
            {confirmAction?.type === 'cancel' ? 'iptal etmek' : 'zimmete almak'} istiyor musunuz?
          </Typography>
          <Stack direction="row" spacing={1.5} justifyContent="center" sx={{ mt: 3 }}>
            <Button
              variant="outlined"
              color="inherit"
              disabled={actionLoading}
              onClick={() => setConfirmAction(null)}
              sx={{ minWidth: 110, color: '#344054' }}
            >
              Vazgeç
            </Button>
            <Button
              variant="contained"
              color={confirmAction?.type === 'cancel' ? 'error' : 'primary'}
              disableElevation
              disabled={actionLoading}
              onClick={runConfirmedAction}
              sx={{ minWidth: 110 }}
            >
              {actionLoading ? <CircularProgress size={20} color="inherit" /> : 'Evet'}
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast?.severity || 'success'} variant="filled" onClose={() => setToast(null)}>
          {toast?.message}
        </Alert>
      </Snackbar>
    </>
  );
}
